"use client";

import { animate } from "motion";
import { m, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useApplicationMutation } from "@trex-2b/hooks";
import { mergeClasses } from "@trex-2b/libs";
import { toCdnAssetUrl } from "@trex-2b/libs/cdn";
import { GROWTH_ROUTES } from "@trex-2b/libs/config/routes";
import { logGAEvent } from "@trex-2b/libs/utils/analytics";
import {
  ONBOARDING_FLOWS,
  reportGrowthPortalError,
} from "@trex-2b/libs/utils/bugsnag-report";
import { useLandingLoginGuard } from "@trex-2b/ui/auth/use-landing-login-guard";

import { StartPrismInput } from "@/app/landing-page/_components/start-prism-input";
import { postPrismAnalyze, savePrismAnalyzeCache } from "@/lib/prism-api";

/** Figma column @2× width 202px → 101px flex-basis for 16 prism stripes. */
const PRISM_COLUMN_FLEX_BASIS_PX = 202 / 2;

/** Figma `30436:3788` — 16-column overlay: CSS gradient + backdrop blur. */
const PRISM_OVERLAY_COLUMN_COUNT = 16;
const PRISM_COLUMN_BACKGROUND =
  "linear-gradient(270deg, rgba(255, 255, 255, 0.01) 0%, rgba(0, 0, 0, 0.2) 76.04%, rgba(255, 255, 255, 0.01) 100%)";

/**
 * Figma StandardDesign `PrismHeroSection` — node `9155:3713`.
 * Layout matches MCP export: `9155:3714` backdrop full viewport below nav (72px), `9155:3734` content `absolute` centered
 * at `left-1/2` / `top-[calc(50%+0.5px)]`, gaps `62` / `13`, stats `9155:3745` (`gap-[24]`, `h-[86]`, `w-[366]` pills).
 */

type StatItemProps = {
  value: string;
  label: string;
  /** Figma `rounded-[100px]` vs third card `rounded-[93px]` (`9155:3754`). */
  roundedClass?: string;
};

function parseAnimatedStat(value: string) {
  const matched = value.trim().match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (!matched) return null;

  const numeric = Number(matched[1]);
  if (Number.isNaN(numeric)) return null;

  const decimalPlaces = matched[1].includes(".")
    ? (matched[1].split(".")[1]?.length ?? 0)
    : 0;

  return {
    target: numeric,
    suffix: matched[2],
    decimalPlaces,
  };
}

function formatAnimatedStatValue(
  parsed: NonNullable<ReturnType<typeof parseAnimatedStat>>,
) {
  const rounded =
    parsed.decimalPlaces > 0
      ? parsed.target.toFixed(parsed.decimalPlaces)
      : `${Math.round(parsed.target)}`;
  return `${rounded}${parsed.suffix}`;
}

function AnimatedStatValue({ value }: { value: string }) {
  const shouldReduceMotion = useReducedMotion() ?? false;
  const parsed = useMemo(() => parseAnimatedStat(value), [value]);
  const finalValue = useMemo(
    () => (parsed ? formatAnimatedStatValue(parsed) : value),
    [parsed, value],
  );
  const [displayValue, setDisplayValue] = useState(() => {
    if (!parsed) return value;
    if (shouldReduceMotion) return finalValue;
    return `${(0).toFixed(parsed.decimalPlaces)}${parsed.suffix}`;
  });
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!parsed) return;
    if (shouldReduceMotion) {
      setDisplayValue(finalValue);
      return;
    }
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    const controls = animate(0, parsed.target, {
      duration: 1.8,
      ease: "circOut",
      onUpdate: (latest) => {
        const rounded =
          parsed.decimalPlaces > 0
            ? latest.toFixed(parsed.decimalPlaces)
            : `${Math.round(latest)}`;
        setDisplayValue(`${rounded}${parsed.suffix}`);
      },
    });

    return () => {
      // React StrictMode (dev) runs mount effects twice.
      // Reset the mount guard so the "real" second mount can start animation.
      hasMountedRef.current = false;
      controls.stop();
    };
  }, [parsed, shouldReduceMotion, finalValue]);

  return <p className="leading-[86px] tabular-nums">{displayValue}</p>;
}

function StatPill({
  value,
  label,
  roundedClass = "rounded-[100px]",
}: StatItemProps) {
  return (
    <div
      className={mergeClasses(
        "flex h-[86px] w-[366px] max-w-full shrink-0 flex-col items-center justify-center overflow-hidden border border-solid border-[rgba(28,25,22,0.6)] bg-[rgba(144,140,129,0)]",
        roundedClass,
      )}
    >
      <div className="flex h-[66px] items-center justify-center gap-[24px] text-center leading-0 not-italic whitespace-nowrap">
        <div
          className={mergeClasses(
            "font-ancizar flex flex-col justify-center text-[48px] font-semibold tracking-[-3.6px] text-[#1c1916] capitalize",
          )}
        >
          <AnimatedStatValue value={value} />
        </div>
        <div
          className={mergeClasses(
            "font-inter flex flex-col justify-center text-[20px] leading-[1.3] font-normal tracking-[-0.1884px] text-[rgba(28,25,22,0.6)]",
          )}
        >
          <p>{label}</p>
        </div>
      </div>
    </div>
  );
}

export function LandingPrismHero() {
  const { createApplication } = useApplicationMutation();
  const { runWithLogin } = useLandingLoginGuard();
  const shouldReduceMotion = useReducedMotion() ?? false;
  const [projectInput, setProjectInput] = useState("");
  const [startError, setStartError] = useState<string | null>(null);
  const [startingFlow, setStartingFlow] = useState(false);

  const handleMatchMyProject = () => {
    runWithLogin(async () => {
      const trimmed = projectInput.trim();
      if (!trimmed || startingFlow) return;

      setStartError(null);
      setStartingFlow(true);
      const startedAt = Date.now();
      try {
        let createResult: Awaited<ReturnType<typeof createApplication>>;
        try {
          createResult = await createApplication(
            {
              intent: "PENDING",
              advertiserUrl: trimmed,
            },
            { context: { source: "landing", project_input: trimmed } },
          );
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : "Failed to start. Try again.";
          setStartError(message);
          return;
        }

        const projectId = createResult.response?.obj?.id;
        if (!projectId) {
          reportGrowthPortalError(
            ONBOARDING_FLOWS.CREATE_APPLICATION,
            new Error("Missing project ID"),
            {
              stage: "missing_project_id",
              source: "landing",
              project_input: trimmed,
            },
          );
          setStartError("Missing project ID. Please retry.");
          return;
        }

        try {
          const analyzeResponse = await postPrismAnalyze(projectId, trimmed);
          savePrismAnalyzeCache(projectId, analyzeResponse);
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : "Failed to analyze project. Try again.";
          logGAEvent("analysis_fail", {
            stage: "prism_analyze",
            project_input: trimmed,
            project_id: projectId,
            duration_ms: Date.now() - startedAt,
            error_message: message,
          });
          setStartError(message);
          return;
        }

        const params = new URLSearchParams({
          step: "audience",
          project: trimmed,
          from: "landing",
        });
        window.open(
          `${GROWTH_ROUTES.ON_BOARDING_PROJECT_HREF(encodeURIComponent(projectId))}?${params.toString()}`,
          "_blank",
        );
      } finally {
        setStartingFlow(false);
      }
    });
  };
  return (
    <section
      aria-labelledby="landing-prism-hero-headline"
      className="relative w-full bg-white"
    >
      {/* `9155:3713`: single grid cell stacks backdrop, prism stripes, then content. */}
      <div className="relative mx-auto grid h-[calc(100dvh-72px)] w-full grid-cols-1 grid-rows-1 overflow-hidden">
        <m.video
          aria-hidden
          className="pointer-events-none col-start-1 row-start-1 z-0 h-full min-h-0 w-full object-cover"
          poster={toCdnAssetUrl("/images/landing/prismHero.webp")}
          playsInline
          muted
          loop={!shouldReduceMotion}
          autoPlay={!shouldReduceMotion}
          preload="auto"
          initial={shouldReduceMotion ? undefined : { opacity: 0, scale: 1.04 }}
          whileInView={
            shouldReduceMotion ? undefined : { opacity: 1, scale: 1 }
          }
          viewport={{ amount: 0.65, once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <source
            src={toCdnAssetUrl("/images/landing/prismHero.mp4")}
            type="video/mp4"
          />
        </m.video>

        {/* Figma `30436:3788` — 16 equal stripes, flex-basis 101px, parent clips. */}
        <div
          aria-hidden
          className="pointer-events-none col-start-1 row-start-1 z-2 flex h-full min-h-0 w-full min-w-0 flex-row"
        >
          {Array.from({ length: PRISM_OVERLAY_COLUMN_COUNT }, (_, index) => {
            return (
              <m.div
                key={`prism-col-${index}`}
                className="h-full min-w-0"
                style={{
                  flex: `1 1 ${PRISM_COLUMN_FLEX_BASIS_PX}px`,
                  background: PRISM_COLUMN_BACKGROUND,
                  backgroundBlendMode: "overlay",
                  backdropFilter: "blur(45.02950668334961px)",
                  WebkitBackdropFilter: "blur(45.02950668334961px)",
                }}
                initial={
                  shouldReduceMotion
                    ? undefined
                    : { opacity: 0, y: index % 2 === 0 ? -18 : 18 }
                }
                whileInView={
                  shouldReduceMotion ? undefined : { opacity: 1, y: 0 }
                }
                viewport={{ amount: 0.65, once: true }}
                transition={{
                  duration: 0.52,
                  delay: 0.04 + index * 0.022,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            );
          })}
        </div>
        {/* `9155:3734` HeroContentColumn */}
        <m.div
          className="pointer-events-auto col-start-1 row-start-1 z-10 absolute top-[calc(50%+0.5px)] left-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[62px] px-4 min-[1512px]:px-0"
          initial={shouldReduceMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={{ amount: 0.6, once: true }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.12, delayChildren: 0.18 },
            },
          }}
        >
          {[
            <m.div
              key="headline"
              className="flex w-[936px] flex-col items-start justify-center gap-[13px] text-center leading-0 not-italic"
              variants={{
                hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
                visible: {
                  opacity: 1,
                  y: 0,
                  filter: "blur(0px)",
                  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              {/* `9155:3735` HeadlineStack */}
              <h1
                className="font-ancizar flex w-full flex-col justify-center text-[72px] font-medium tracking-[-3.6px] text-[#1c1916] max-[1511px]:text-[clamp(2.25rem,6vw,4.5rem)] max-[1511px]:leading-[1.1] max-[1511px]:tracking-tighter capitalize"
                id="landing-prism-hero-headline"
              >
                <span className="leading-[86px] max-[1511px]:leading-[1.15]">
                  Match The Right Users
                  <br aria-hidden />
                  Watch Your Project Shine
                </span>
              </h1>
              <div className="font-inter flex w-full flex-col justify-center text-[18px] leading-[24px] font-normal tracking-[-0.3px] text-[rgba(28,25,22,0.8)]">
                <p>
                  Users leave trails across online platforms.
                  <br aria-hidden />
                  PRISM scores them from every angle and hands the best fits for
                  your project.
                </p>
              </div>
            </m.div>,
            <m.div
              key="start"
              variants={{
                hidden: { opacity: 0, y: 24 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.62, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              <StartPrismInput
                key="start"
                prefix={"@"}
                placeholder="X Handle name of your project"
                autoComplete="off"
                className="w-[936px]"
                value={projectInput}
                onChange={(event) => {
                  setProjectInput(event.target.value);
                  if (startError) setStartError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleMatchMyProject();
                  }
                }}
                onActionClick={handleMatchMyProject}
                actionDisabled={!projectInput.trim() || startingFlow}
                loading={startingFlow}
                errorMessage={startError ?? undefined}
              />
            </m.div>,
            <m.div
              key="stats"
              className="flex max-w-full shrink-0 flex-col items-center gap-6 min-[1158px]:h-[86px] min-[1158px]:flex-row min-[1158px]:gap-[24px]"
              variants={{
                hidden: { opacity: 0, y: 28 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.62,
                    ease: [0.16, 1, 0.3, 1],
                    staggerChildren: 0.08,
                  },
                },
              }}
            >
              {/* `9155:3745` StatsRow */}
              <m.div
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <StatPill value="310K+" label="Verified Profiles" />
              </m.div>
              <m.div
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <StatPill value="70K+" label="Active Daily" />
              </m.div>
              <m.div
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <StatPill
                  value="2.1M+"
                  label="Verified Actions"
                  roundedClass="rounded-[93px]"
                />
              </m.div>
            </m.div>,
          ]}
        </m.div>
      </div>
    </section>
  );
}
